import { isDefined } from "@clipboard-health/util-ts";
import { useGetWorkplace } from "@src/appV2/FacilityProfile/api/useGetWorkplace";

import { useCurrentUserId } from "../../../ExperimentalHomeHealth/lib/useCurrentUserId";

// eslint-disable-next-line no-restricted-syntax
export enum ChartingCredentialStage {
  OFF = "OFF",
  CHARTING_ONLY = "CHARTING_ONLY",
  CHARTING_AND_TIME_CLOCK = "CHARTING_AND_TIME_CLOCK",
  TIME_CLOCK_ONLY = "TIME_CLOCK_ONLY",
}

interface UseCredentialSharingResult {
  isEnabled: boolean;
  isChartingOnly: boolean;
  isChartingAndTimeClock: boolean;
  isTimeClockOnly: boolean;
  stage: ChartingCredentialStage;
  enableOnboardingView: boolean;
  shiftStartingSoonThresholdInHours: number;
}

const DEFAULT_SHIFT_STARTING_SOON_THRESHOLD_IN_HOURS = 24;

function deriveStage(
  chartingEnabled: boolean,
  timekeepingEnabled: boolean
): ChartingCredentialStage {
  if (chartingEnabled && timekeepingEnabled) {
    return ChartingCredentialStage.CHARTING_AND_TIME_CLOCK;
  }

  if (chartingEnabled) {
    return ChartingCredentialStage.CHARTING_ONLY;
  }

  if (timekeepingEnabled) {
    return ChartingCredentialStage.TIME_CLOCK_ONLY;
  }

  return ChartingCredentialStage.OFF;
}

/**
 * Reads credential sharing configuration from the workplace profile's
 * `workerCredentialConfiguration` field. This replaces the previous
 * LaunchDarkly CREDENTIAL_SHARING feature flag — the configuration now
 * lives in the database and is managed via the workplace settings page.
 *
 * The return type is kept unchanged so that existing consumers are not impacted.
 */
export function useCredentialSharing(): UseCredentialSharingResult {
  const workplaceId = useCurrentUserId();
  const { data: workplaceResponse } = useGetWorkplace(
    { workplaceId },
    { enabled: isDefined(workplaceId) }
  );

  const config = workplaceResponse?.data.attributes.workerCredentialConfiguration;

  const chartingEnabled = config?.charting.enabled ?? false;
  const timekeepingEnabled = config?.timekeeping.enabled ?? false;
  const stage = deriveStage(chartingEnabled, timekeepingEnabled);

  return {
    isEnabled: stage !== ChartingCredentialStage.OFF,
    isChartingOnly: stage === ChartingCredentialStage.CHARTING_ONLY,
    isChartingAndTimeClock: stage === ChartingCredentialStage.CHARTING_AND_TIME_CLOCK,
    isTimeClockOnly: stage === ChartingCredentialStage.TIME_CLOCK_ONLY,
    stage,
    enableOnboardingView: config?.onboardingView.enabled ?? false,
    shiftStartingSoonThresholdInHours:
      config?.onboardingView.shiftStartingSoonThreshold.value ??
      DEFAULT_SHIFT_STARTING_SOON_THRESHOLD_IN_HOURS,
  };
}
